import isEqual from 'lodash.isequal';
import uniqWith from 'lodash.uniqwith';
import { match } from 'ts-pattern';
import { v1 } from 'uuid';
import { DataValue } from '../api/types';
import {
  ApplicationDataFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  JobDataFields,
  MetricIdType,
  OfferDataFields,
  Segmentation,
} from '../types';
import {
  applicationCurrentStageValuesTranslationMap,
  applicationStatusValuesTranslationMap,
  employmentRegularityTranslationKeyMap,
  genderTranslationKeyMap,
  hireStatusValuesTranslationMap,
  jobStatusValuesTranslationMap,
  managerOrICTranslationKeyMap,
  maritalStatusTranslationKeyMap,
  regionalLocalTranslationKeyMap,
  regretNonRegretTranslationKeyMap,
  voluntaryNonVoluntaryTranslationKeyMap,
} from './translations';
import {
  ApplicationCurrentStateValues,
  ApplicationStatusValues,
  EmploymentRegularityValues,
  GenderValues,
  HireStatusValues,
  JobStatusValues,
  ManagerOrIcValues,
  MartialStatusValues,
  RegionalOrLocalValues,
  RegretNonRegretValues,
  SortTypes,
  VoluntaryInvoluntaryValues,
} from './types';

export const uuid = () => v1();

export const mergeSegmentations = (
  s1: Segmentation[] | undefined,
  s2: Segmentation[] | undefined,
  metrics: MetricIdType[],
  maxSegmentationLevel: number
): Segmentation[] | undefined => {
  if (metrics.length > 1) {
    return s1 && s1.length > 0 ? s1 : undefined;
  }
  const ss: Segmentation[] = [...(s1 ?? []), ...(s2 ?? [])];
  if (ss.length > 0 && ss.length <= maxSegmentationLevel) {
    return ss;
  } else {
    return s1 && s1.length > 0 ? s1 : undefined;
  }
};

export const shortenLabel = (label: string, maxChars = 10): string => {
  const shortenedSegment = label.substring(0, maxChars);
  return label.length > maxChars ? shortenedSegment : label;
};

// TODO: could use a generic type for label so that we don't have to cast
// but that means using GenderValues, ManagerOrIcValues, etc when parsing the backend response
export const translateLabel = (
  label: DataValue,
  dataField: DataFieldWithDataType
): string | null => {
  return match(dataField)
    .with(
      { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.GENDER },
      () => genderTranslationKeyMap[label as GenderValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.MANAGER_OR_IC,
      },
      () => managerOrICTranslationKeyMap[label as ManagerOrIcValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.DEFINED_MANAGER,
      },
      () => managerOrICTranslationKeyMap[label as ManagerOrIcValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.MARITAL_STATUS,
      },
      () => maritalStatusTranslationKeyMap[label as MartialStatusValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.REGIONAL_OR_LOCAL,
      },
      () => regionalLocalTranslationKeyMap[label as RegionalOrLocalValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.LOCAL_OR_NON_LOCAL,
      },
      () => regionalLocalTranslationKeyMap[label as RegionalOrLocalValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.REGRET_ATTRITION,
      },
      () => regretNonRegretTranslationKeyMap[label as RegretNonRegretValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.VOLUNTARY_OR_INVOLUNTARY_ATTRITION,
      },
      () =>
        voluntaryNonVoluntaryTranslationKeyMap[
          label as VoluntaryInvoluntaryValues
        ]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.EMPLOYMENT_REGULARITY,
      },
      () =>
        employmentRegularityTranslationKeyMap[
          label as EmploymentRegularityValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.STATUS,
      },
      () =>
        applicationStatusValuesTranslationMap[label as ApplicationStatusValues]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.STANDARDIZED_CURRENT_STAGE,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE_LEVEL_1,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE_LEVEL_2,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE_LEVEL_3,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      { dataType: DataTypes.JOB, dataField: JobDataFields.STATUS },
      () => jobStatusValuesTranslationMap[label as JobStatusValues]
    )
    .with(
      { dataType: DataTypes.OFFER, dataField: OfferDataFields.STATUS },
      () => hireStatusValuesTranslationMap[label as HireStatusValues]
    )
    .otherwise(() => null);
};

export const delay = (delayInms: number) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms));
};

export const sortTypeTranslationKeys: Record<SortTypes, string> = {
  [SortTypes.ASC]: 'common:commonValues.sortValues.az',
  [SortTypes.DESC]: 'common:commonValues.sortValues.za',
  [SortTypes.HIGH]: 'common:commonValues.sortValues.highLow',
  [SortTypes.LOW]: 'common:commonValues.sortValues.lowHigh',
} as const;

export const removeDuplicates = <T>(arr: T[]) => uniqWith(arr, isEqual);

export const arrayStartsWith = <T>(array1: T[], array2: T[]) => {
  if (array2.length > array1.length) {
    return false;
  } else {
    return array2.every((item, index) => item === array1[index]);
  }
};

export const getDeltaSign = (delta: number) => {
  // since minus is already shown in the value, we don't need to show it again
  return delta >= 0 ? '+' : '';
};

// Create true object reference-based memoization (single argument only)
export const memoizeByReference = <T extends object, R>(
  fn: (arg: T) => R
): ((arg: T) => R) => {
  const cache = new WeakMap<T, R>();

  return function (this: any, arg: T): R {
    if (cache.has(arg)) {
      return cache.get(arg)!;
    }

    const result = fn.apply(this, [arg]);
    cache.set(arg, result);
    return result;
  };
};
